import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core'
import { MomentPipe } from '../../../pipes/moment.pipe'
import { EpgService } from '../../../services/epg.service'
import { LocalChannel } from '../../../interfaces/local-channel'
import { LocalEPGProgram } from '../../../interfaces/local-epg'
import { NgForOf, NgIf } from '@angular/common'
import moment from 'moment/moment'
import { interval, Subscription } from 'rxjs'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { ProgramTitlePipe } from '../../../pipes/program-title.pipe'
import { ProgramDescriptionPipe } from '../../../pipes/program-description.pipe'

interface IPGProgram {
  width: number
  offset: number
  categoryColor?: string
  start: Date
  end: Date
  program?: LocalEPGProgram
}

interface IPGChannel {
  channel: LocalChannel
  programs: IPGProgram[]
  rowWidth: number
}

interface IPGTime {
  offset: number
  value: string
}

/*
 * Uncomment to test with categories but for now we don't have categories coming from the EPG in htv-ms
 */
/*
const _categories: { name: string; color: string }[] = [
  { name: 'Journal TV', color: 'dodgerblue' },
  { name: "Emissions d'information", color: 'midnightblue' },
  { name: 'Cinéma', color: 'orangered' },
  { name: 'Sport', color: 'forestgreen' },
  { name: 'Séries', color: 'chocolate' },
  { name: 'Magazine', color: 'blueviolet' },
]
*/

@Component({
  selector: 'app-ipg',
  standalone: true,
  imports: [MomentPipe, NgForOf, MatButtonModule, MatIconModule, NgIf, ProgramTitlePipe, ProgramDescriptionPipe],
  templateUrl: './ipg.component.html',
  styleUrls: ['./ipg.component.scss'],
})
export class IPGComponent implements OnInit, OnDestroy {
  locale = moment.locale()
  subscriptions: Subscription = new Subscription()

  channels!: LocalChannel[]
  channelsIPG!: { [key: string]: IPGChannel }

  startDate = moment()
  minDate!: moment.Moment
  maxDate!: moment.Moment
  currentScroll = 0
  currentScrollTime = moment()
  minOffset = 0

  times: IPGTime[] = []
  timelineOffset = 40

  readonly stepSize = 8 // 1 step = 1 minute
  readonly scrollStep = this.stepSize * 15 // 15 minutes
  readonly centerOffset = 256 // Avoid positioning full left
  readonly marginOffset = 12 // $ipg-row-padding * 2 from scss
  readonly finalProgramWidth = 1000

  cursorOffset!: number
  cursorTime!: string

  selectedChannel!: LocalChannel
  selectedProgram!: IPGProgram
  selectedProgramIndex!: number

  categories: { name: string; color: string }[] = []

  @ViewChild('timeline') timeline!: ElementRef<HTMLDivElement>
  @ViewChildren('program') programs!: QueryList<ElementRef>

  constructor(private epgService: EpgService) {
    // do nothing
  }

  ngOnInit() {
    const channelsEPG = this.epgService.channelsEPG
    this.channels = this.epgService.channels.filter(channel => channelsEPG[channel.id]?.programs.length > 0)

    let minOffset = 99999
    let maxRowWidth = 0
    let minStart = moment()
    let maxEnd = moment()

    const channelsIPG: { [key: string]: IPGChannel } = {}

    for (let i = 0; i < this.channels.length; i++) {
      const channel = this.channels[i]
      let rowWidth = 0
      const programs = channelsEPG[channel.id].programs
        .filter(program => program.duration)
        .map(program => {
          const width = program.duration * this.stepSize - this.marginOffset
          rowWidth += width
          const momentStart = moment(program.start)
          const momentEnd = moment(program.end)
          const offset = this.getOffset(momentStart)
          if (offset < minOffset) {
            minOffset = offset
          }
          if (momentStart.isBefore(minStart)) {
            minStart = momentStart
          }
          if (momentEnd.isAfter(maxEnd)) {
            maxEnd = momentEnd
          }

          const ipgProgram: IPGProgram = {
            width: width,
            offset: offset,
            start: program.start,
            end: program.end,
            program: program,
          }

          // TEST: Assigning random category
          // Uncomment to test
          /*
          const random = _categories.length * Math.random() << 0
          const category = _categories[random]
          program.category = category.name
          if (program.category) {
            const index = this.categories.findIndex(c => c.name === program.category)
            if (index < 0) {
              this.categories.push(_categories.find(c => c.name === program.category)!)
            }
          }
          ipgProgram.categoryColor = category.color
          */

          return ipgProgram
        })
      channelsIPG[channel.id] = {
        channel: channel,
        programs: programs,
        rowWidth: rowWidth,
      }

      if (rowWidth > maxRowWidth) {
        maxRowWidth = rowWidth
      }
    }

    // Add final program that will be hidden for better display
    for (const channelId in channelsIPG) {
      const ipgChannel = channelsIPG[channelId]
      const programs = ipgChannel.programs
      const lastProgram = programs[programs.length - 1]
      const hiddenProgramWidth = maxRowWidth - ipgChannel.rowWidth + this.finalProgramWidth
      ipgChannel.programs.push({
        width: hiddenProgramWidth,
        offset: lastProgram.offset + lastProgram.width,
        categoryColor: lastProgram.categoryColor,
        start: new Date(),
        end: new Date(),
      })
    }

    this.channelsIPG = channelsIPG
    this.minOffset = minOffset
    this.minDate = minStart
    this.maxDate = maxEnd

    // Generate timeline times
    const timesMinStart = moment(this.minDate)
    const timesMaxEnd = moment(this.maxDate)
    for (
      let i = timesMinStart, steps = timesMinStart.diff(timesMaxEnd, 'minutes') / 30;
      i <= timesMaxEnd;
      i = i.add(30, 'minutes'), steps++
    ) {
      const momentDate = moment(i)
      const offset = momentDate.diff(this.startDate, 'minutes', true) * this.stepSize
      this.times.push({
        offset: offset,
        value: momentDate.format('LT'),
      })
    }

    // Find first program to focus
    const now = moment()
    const firstChannel = this.channels[0]
    const firstProgramIndex = this.channelsIPG[firstChannel.id].programs.findIndex(ipgProgram => {
      return now.isAfter(ipgProgram.start) && now.isBefore(ipgProgram.end)
    })
    this.selectedChannel = firstChannel
    this.selectedProgram = this.channelsIPG[firstChannel.id].programs[firstProgramIndex]
    this.selectedProgramIndex = firstProgramIndex

    // Cursor initialization
    this.subscriptions.add(
      interval(1000).subscribe(() => {
        const m = moment()
        this.cursorOffset = this.getOffset(m)
        this.cursorTime = m.format('LT')
      })
    )
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
  }

  getOffset(date: moment.Moment): number {
    return date.diff(this.startDate, 'minutes', true) * this.stepSize
  }

  onClick(event: any, channel: LocalChannel, ipgProgram: IPGProgram, ipgProgramIndex: number): void {
    this.selectedChannel = channel
    this.selectedProgram = ipgProgram
    this.selectedProgramIndex = ipgProgramIndex
    this.scroll(event.target.offsetLeft - this.centerOffset)
  }

  goToPreviousDay(): void {
    let previousDay = moment(this.currentScrollTime).subtract(1, 'day')
    if (previousDay.isBefore(this.minDate)) {
      previousDay = this.minDate
    }
    const previousDayOffset = this.getOffset(previousDay)
    this.scroll(previousDayOffset - this.minOffset)
  }

  goToNextDay(): void {
    let nextDay = moment(this.currentScrollTime).add(1, 'day')
    if (nextDay.isAfter(this.maxDate)) {
      nextDay = this.maxDate
    }
    const nextDayOffset = this.getOffset(nextDay)
    this.scroll(nextDayOffset - this.minOffset)
  }

  scrollLeft(): void {
    this.scroll(this.currentScroll - this.scrollStep)
  }

  scrollRight(): void {
    this.scroll(this.currentScroll + this.scrollStep)
  }

  scroll(value: number): void {
    // Apply offset to timeline
    this.timeline.nativeElement.scrollLeft = value

    // Apply offset to all rows
    this.programs.forEach(program => {
      program.nativeElement.scrollLeft = value
    })

    this.currentScroll = value
    const currentScrollMinutes = (this.currentScroll + this.centerOffset) / this.stepSize
    this.currentScrollTime = moment(this.minDate).add(currentScrollMinutes, 'minutes')
  }
}
